import gsap from 'gsap';

/**
 * THE render loop. Singular.
 *
 * It is deliberately built on `gsap.ticker` rather than on its own
 * `requestAnimationFrame`. GSAP already runs a rAF for its tweens; adding a
 * second one means two callbacks per frame, two chances to read layout, and
 * writes landing in an order that depends on which loop registered first. One
 * ticker keeps every read and write in a single, predictable pass.
 *
 * If you ever find yourself writing `requestAnimationFrame` anywhere else in
 * this client, subscribe here instead.
 */

export type FrameCallback = (elapsedMs: number, deltaMs: number, frame: number) => void;

class RenderLoop {
  private readonly callbacks = new Set<FrameCallback>();
  private running = false;
  private frame = 0;

  private readonly tick = (time: number, deltaMs: number): void => {
    this.frame += 1;
    // Snapshot: a callback that unsubscribes mid-iteration must not skip its
    // neighbour.
    for (const callback of [...this.callbacks]) {
      callback(time * 1000, deltaMs, this.frame);
    }
  };

  subscribe(callback: FrameCallback): () => void {
    this.callbacks.add(callback);
    this.start();
    return () => this.unsubscribe(callback);
  }

  unsubscribe(callback: FrameCallback): void {
    this.callbacks.delete(callback);
    if (this.callbacks.size === 0) this.stop();
  }

  /** Frames per second GSAP is targeting. -1 means "match the display". */
  setFps(fps: number): void {
    gsap.ticker.fps(fps);
  }

  private start(): void {
    if (this.running) return;
    gsap.ticker.add(this.tick);
    this.running = true;
  }

  private stop(): void {
    if (!this.running) return;
    gsap.ticker.remove(this.tick);
    this.running = false;
  }

  get subscriberCount(): number {
    return this.callbacks.size;
  }
}

export const renderLoop = new RenderLoop();
